import { useEffect, useMemo, useState } from 'react';

/**
 * Client-side search / filter / pagination for admin tables.
 *
 * @param {Array}  items                 The raw list.
 * @param {Object} opts
 * @param {Function} [opts.searchFn]     (item, lowerQuery) => boolean
 * @param {Object<string,Function>} [opts.filters]
 *        Map of filterKey -> (item, value) => boolean.
 * @param {number} [opts.pageSize=10]
 *
 * Returns {
 *   query, setQuery,
 *   filterValues, setFilter,
 *   page, setPage, pageSize, setPageSize, pageCount,
 *   total, filteredCount,
 *   pageItems, filteredItems,
 *   reset,
 * }
 */
export default function useTableControls(items, opts = {}) {
  const { searchFn, filters = {}, pageSize: initialPageSize = 10 } = opts;
  const [query, setQuery] = useState('');
  const [filterValues, setFilterValues] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    return list.filter((it) => {
      if (q && searchFn && !searchFn(it, q)) return false;
      for (const [k, fn] of Object.entries(filters)) {
        const v = filterValues[k];
        if (v == null || v === '' || v === 'all') continue;
        if (!fn(it, v)) return false;
      }
      return true;
    });
  }, [list, query, filterValues, searchFn, filters]);

  const pageCount = Math.max(1, Math.ceil(filteredItems.length / pageSize));

  // Clamp page if filters/items shrink past current page
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, pageSize]);

  const setFilter = (key, value) => {
    setFilterValues((s) => ({ ...s, [key]: value }));
    setPage(1);
  };

  const updateQuery = (v) => { setQuery(v); setPage(1); };
  const updatePageSize = (n) => { setPageSize(n); setPage(1); };
  const reset = () => { setQuery(''); setFilterValues({}); setPage(1); };

  return {
    query, setQuery: updateQuery,
    filterValues, setFilter,
    page, setPage,
    pageSize, setPageSize: updatePageSize,
    pageCount,
    total: list.length,
    filteredCount: filteredItems.length,
    pageItems, filteredItems,
    reset,
  };
}
